// src/pages/api/ekdAPI.ts — holistic Enterprise-Knowledge SME review API.
import { apiGet, apiPost, api as apiClient } from "../API";
const BASE = "/adriva_api/ekd";

export type LaneKey = "fields" | "structure" | "reference" | "rules" | "context";

export interface ReviewItem {
  id: string; type: string; title: string; subtitle?: string; detail?: string;
  question?: string; kind?: string;
  status: "pending" | "confirmed" | "flagged"; note?: string; value?: any;
  editable?: boolean; lockable?: boolean; empty?: boolean; value_count?: number;
  seed_confirmed?: boolean; field?: string;
  why?: string; confirm_label?: string; flag_label?: string; readonly?: boolean;
  badge?: string; gap_type?: string;
}
export interface LaneCounts { total: number; confirmed: number; flagged: number; pending: number; }
export interface LaneResponse {
  success: boolean; entity_id: string; lane: LaneKey;
  groups: Record<string, ReviewItem[]>; readonly_groups: string[]; counts: LaneCounts;
}
export interface EkdSummary {
  success: boolean; entity_id: string; entity?: string; domain?: string;
  lanes: Record<LaneKey, LaneCounts>;
  totals: { decisions: number; confirmed: number };
  governed_reference_domains: number;
  pending_reference_domains?: number;
  readiness: { baseline_business_readiness?: number; sme_decided_share: number; note?: string; };
}

export function ekd_getSummary(entityId: string): Promise<EkdSummary> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/summary`);
}
export function ekd_getLane(entityId: string, lane: LaneKey): Promise<LaneResponse> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/lane/${lane}`);
}
export function ekd_refreshReference(entityId: string): Promise<{ success: boolean; reference_domains: number; values: number; note?: string }> {
  return apiPost(`${BASE}/${encodeURIComponent(entityId)}/refresh-reference-domains`, {});
}
export function ekd_review(entityId: string, item_type: string, item_id: string,
                          status: string, value: any = null, note = ""): Promise<LaneResponse> {
  return apiPost(`${BASE}/${encodeURIComponent(entityId)}/review`, { item_type, item_id, status, value, note });
}

/* ── Structure editor: the table's columns + live measurement of the key / join being edited ── */
export interface EkdColumn { column: string; field_id: string; label?: string | null; fill_pct?: number | null; distinct?: number | null; unique_alone_pct?: number | null }
export interface EkdColumns { table: string; tables: string[]; columns: EkdColumn[] }
export interface GrainMeasure {
  ok: boolean; reason?: string; table?: string; columns?: string[]; rows?: number; rows_key_filled?: number;
  unique_pct?: number | null; duplicate_rows?: number; duplicate_groups?: number; is_unique?: boolean;
  example_duplicates?: Record<string, any>[];
}
export interface JoinMeasure {
  ok: boolean; reason?: string; child?: string; parent?: string; pairs?: string[][]; child_rows?: number;
  child_rows_key_filled?: number; match_pct?: number | null; unmatched_rows?: number; parent_key_unique?: boolean;
  cardinality?: string; suggested_pairs?: { child: string; parent: string; share_pct: number }[];
}
export async function ekd_columns(entityId: string, table: string): Promise<EkdColumns> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/structure/columns/${encodeURIComponent(table)}`);
}
export async function ekd_measureGrain(entityId: string, table: string, columns: string[]): Promise<GrainMeasure> {
  return apiPost(`${BASE}/${encodeURIComponent(entityId)}/structure/measure`, { kind: "grain", table, columns }, { timeout: 5 * 60 * 1000 });
}
export async function ekd_measureJoin(entityId: string, child: string, parent: string, pairs: string[][]): Promise<JoinMeasure> {
  return apiPost(`${BASE}/${encodeURIComponent(entityId)}/structure/measure`, { kind: "join", child, parent, pairs }, { timeout: 5 * 60 * 1000 });
}

/* ── Knowledge sources (catalog exports, business documents) - evidence for the next EK discovery run ── */
export interface KnowledgeSource { file_name: string; type?: string; chunks?: number; chunks_with_fields?: number; fields?: number; error?: string; skipped_value_lists?: string[]; not_read_yet?: boolean }
export interface KnowledgeSources { entity_id: string; sources: KnowledgeSource[]; built_at?: string; chunks: number; fields_with_evidence: number }
export async function ekd_listSources(entityId: string): Promise<KnowledgeSources> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/sources`);
}
export async function ekd_uploadSource(entityId: string, file: File): Promise<KnowledgeSources> {
  const fd = new FormData(); fd.append("file", file);
  const res: any = await apiClient.post(`${BASE}/${encodeURIComponent(entityId)}/sources`, fd, { timeout: 10 * 60 * 1000 });
  return (res?.data ?? res) as KnowledgeSources;
}
export async function ekd_deleteSource(entityId: string, fileName: string): Promise<KnowledgeSources> {
  const res: any = await apiClient.delete(`${BASE}/${encodeURIComponent(entityId)}/sources/${encodeURIComponent(fileName)}`);
  return (res?.data ?? res) as KnowledgeSources;
}
export async function ekd_rebuildSources(entityId: string): Promise<KnowledgeSources> {
  return apiPost(`${BASE}/${encodeURIComponent(entityId)}/sources/rebuild`, {}, { timeout: 10 * 60 * 1000 });
}

/* ── Field review: every field's knowledge card, attribute by attribute ── */
export interface FieldRow { field_id: string; name?: string | null; in_scope: boolean; attributes: number; client: number; open: number; decided: number; conflicts: number; warnings: number; auto_accepted?: number; dismissed?: number; protection?: string | null }
export interface FieldList { entity_id: string; fields: FieldRow[]; scope_defined: boolean; summary: { fields: number; in_scope: number; open: number; decided: number; auto_accepted?: number; dismissed?: number; attributes?: number; protected?: number } }
export interface Citation { id?: string; text?: string; source?: { file_name?: string; sheet?: string; row?: number; page?: number } }
export interface CardRow {
  attribute: string; label: string; value: any; authority?: string | null; source?: string | null; confidence?: number | null;
  citation?: Citation | null; alternatives: { value: any; authority?: string; source?: string; citation?: Citation | null }[];
  verification?: { method?: string; support?: number; status?: string; model?: string; reason?: string; corrected_value?: any } | null;
  conflict: boolean; warning: boolean; decision?: { status?: string; value?: any; by?: string } | null;
  bucket?: "auto_accepted" | "baseline" | "auto_dismissed" | "needs_sme"; bucket_reason?: string | null; role?: string | null;
}
export interface FieldCard { field_id: string; name?: string | null; description?: string | null; groups: { group: string; rows: CardRow[] }[] }
export async function ekd_fields(entityId: string): Promise<FieldList> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/fields`);
}
export async function ekd_fieldCard(entityId: string, fieldId: string): Promise<FieldCard> {
  return apiGet(`${BASE}/${encodeURIComponent(entityId)}/fields/${encodeURIComponent(fieldId)}`);
}
export async function ekd_decideAttr(entityId: string, fieldId: string, attribute: string, status: string, value?: any, by?: string, role?: string): Promise<FieldCard> {
  return apiPost(`${BASE}/${encodeURIComponent(entityId)}/fields/${encodeURIComponent(fieldId)}/decide`, { attribute, status, value, reviewed_by: by, role });
}
export async function ekd_confirmClient(entityId: string, fieldId: string): Promise<FieldCard> {
  return apiPost(`${BASE}/${encodeURIComponent(entityId)}/fields/${encodeURIComponent(fieldId)}/confirm-client`, {});
}
export async function ekd_confirmField(entityId: string, fieldId: string, by: string, role: string): Promise<FieldCard> {
  return apiPost(`${BASE}/${encodeURIComponent(entityId)}/fields/${encodeURIComponent(fieldId)}/confirm-field`, { reviewed_by: by, role });
}
